import { useRef, type KeyboardEvent } from 'react';

export interface SekmeTanimi<T extends string> {
  anahtar: T;
  ad: string;
  /** Sekme adinin yanindaki kirmizi sayi; 0 ya da yoksa cizilmez. */
  rozet?: number;
  /** Rozetin ekran okuyucu etiketi, ör. "3 uyarı". */
  rozetEtiketi?: string;
}

interface Props<T extends string> {
  sekmeler: ReadonlyArray<SekmeTanimi<T>>;
  secili: T;
  onSec: (s: T) => void;
}

/** Konu sekmeleri (Satis Analiz, Stok Analizi). Dar ekranda satir icinde yana kayar; ok tuslari sekmeler arasinda gezer. */
export function SekmeSeridi<T extends string>({ sekmeler, secili, onSec }: Props<T>) {
  const kok = useRef<HTMLDivElement>(null);

  const tus = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const n = sekmeler.length;
    const hedef = sekmeler[(i + (e.key === 'ArrowRight' ? 1 : n - 1)) % n];
    onSec(hedef.anahtar);
    kok.current?.querySelector<HTMLButtonElement>(`[data-testid="sekme-${hedef.anahtar}"]`)?.focus();
  };

  return (
    <div
      ref={kok}
      role="tablist"
      aria-label="Analiz konusu"
      data-testid="sekme-seridi"
      className="flex gap-1 overflow-x-auto border-b border-border"
    >
      {sekmeler.map((s, i) => {
        const aktif = s.anahtar === secili;
        return (
          <button
            key={s.anahtar}
            type="button"
            role="tab"
            id={`sekme-${s.anahtar}`}
            data-testid={`sekme-${s.anahtar}`}
            aria-selected={aktif}
            aria-controls="sekme-paneli"
            tabIndex={aktif ? 0 : -1}
            onClick={() => onSec(s.anahtar)}
            onKeyDown={(e) => tus(e, i)}
            // Alt cizgi golgeyle: dugme kenarligi genel stilde sifirlaniyor (olculdu: border-bottom 0 px).
            className={`focus-ring shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium ${
              aktif ? 'text-ink-900 shadow-[inset_0_-2px_0_currentColor]' : 'text-ink-500 hover:text-ink-900'
            }`}
          >
            {s.ad}
            {!!s.rozet && (
              <span
                data-testid="sekme-rozet"
                aria-label={s.rozetEtiketi}
                className="tnum rounded-full bg-neg-50 px-1.5 text-[11px] font-semibold text-neg-700"
              >
                {s.rozet}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
